import { useUnsafeCallback, useUnsafeMemo } from '@factorialco/deprecated-design-system'
import { DateRange, OneDateNavigator } from '@factorialco/f0-react/dist/experimental'
import moment from 'moment'

import i18n from 'modules/core/lib/i18n'

import {
  formatDate,
  useFinancialWorkspace,
} from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { InsightTableType } from 'modules/finance/components/FinancialWorkspace/types/table'

enum GRANULARITIES {
  year = 'year',
  month = 'month',
  quarter = 'quarter',
}

export const DateNavigator = () => {
  const {
    state,
    dispatch,
    formattedState: { formattedCompareToEndDate, formattedCompareToStartDate },
  } = useFinancialWorkspace()

  const startDate = state.selectedRange[0]
  const endDate = state.selectedRange[1]

  const defaultValue = useUnsafeMemo(
    () => ({
      granularity: GRANULARITIES.year,
      value: { from: startDate, to: endDate },
    }),
    [] // Empty dependencies to make sure default value is only set once and avoid the date navigator reverting to a new default value every time a selection is made.
  )

  const granularities = useUnsafeMemo(
    () => [GRANULARITIES.month, GRANULARITIES.quarter, GRANULARITIES.year],
    []
  )

  const showCompareTo = state.insightTableType === InsightTableType.EmployeeCosts

  const comparisons = useUnsafeMemo(
    () => ({
      month: [
        {
          value: { delta: -1, units: GRANULARITIES.month },
          label: i18n.t(
            'finance.financial_workspace.employee_costs.date_comparisons.previous_month'
          ),
        },
        {
          value: { delta: -12, units: GRANULARITIES.month },
          label: i18n.t(
            'finance.financial_workspace.employee_costs.date_comparisons.previous_year'
          ),
        },
      ],
      quarter: [
        {
          value: { delta: -4, units: GRANULARITIES.quarter },
          label: i18n.t(
            'finance.financial_workspace.employee_costs.date_comparisons.previous_year'
          ),
        },
      ],
      year: [
        {
          value: { delta: -1, units: GRANULARITIES.year },
          label: i18n.t(
            'finance.financial_workspace.employee_costs.date_comparisons.previous_year'
          ),
        },
      ],
    }),
    []
  )

  const compareTo = useUnsafeMemo(
    () => (showCompareTo ? comparisons : undefined),
    [comparisons, showCompareTo]
  )

  const onSelect = useUnsafeCallback(
    (selected) => {
      const dateRange = selected?.value
      if (dateRange) {
        dispatch({ type: 'SET_RANGE', payload: [dateRange.from, dateRange.to] })
      }
    },
    [dispatch]
  )

  const onCompareToChange = useUnsafeCallback(
    (dateRange?: Required<DateRange> | Required<DateRange>[]) => {
      if (dateRange && !Array.isArray(dateRange) && dateRange.to !== undefined) {
        if (
          !(
            formattedCompareToEndDate === formatDate(dateRange.to) &&
            formattedCompareToStartDate === formatDate(dateRange.from)
          )
        ) {
          dispatch({
            type: 'SET_COMPARE_TO_RANGE',
            payload: [dateRange.from, dateRange.to],
          })
        }
      } else if (state.compareToRange !== undefined) {
        dispatch({ type: 'SET_COMPARE_TO_RANGE', payload: undefined })
      }
    },
    [dispatch, state.compareToRange, formattedCompareToEndDate, formattedCompareToStartDate]
  )

  return (
    <OneDateNavigator
      hideGoToCurrent
      onSelect={onSelect}
      compareTo={compareTo}
      defaultValue={defaultValue}
      granularities={granularities}
      onCompareToChange={onCompareToChange}
      maxDate={moment().endOf('year').toDate()}
    />
  )
}
